import Table from '@eduzz/houston-ui/Table';
import Alert from 'components/Alert';
import React, { memo, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useCallbackObservable, useObservable } from 'react-use-observable';
import { of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import formatDocument from 'v2/components/Shared/Masks/document';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import whitelistService from 'v2/services/whitelist';

const List = () => {
  const [isLoading] = useObservable(() => whitelistService.isLoading(), []);
  const [whitelists] = useObservable(() => whitelistService.getWhiteLists(), []);
  const [pagination] = useObservable(() => whitelistService.getPaginationResponse(), []);

  const onChangePerPage = useCallback(
    (per_page: number) => {
      whitelistService.setPagination({ ...pagination, per_page, current_page: 1 });
    },
    [pagination]
  );

  const onChangePage = useCallback(
    (page: number) => {
      whitelistService.setPagination({ ...pagination, current_page: page });
    },
    [pagination]
  );

  const openDialog = useCallback((whitelist) => {
    whitelistService.setCurrentWhiteList(whitelist);
    whitelistService.setCreateAndEditDialog(true);
  }, []);

  const [deleteWhitelist] = useCallbackObservable((whitelist) => {
    return whitelistService.delete(whitelist).pipe(
      tap(() => toast.success('Cliente removido com sucesso.')),
      catchError(() => {
        toast.error('Ocorreu um erro ao tentar remover cliente');
        return of(true);
      })
    );
  }, []);

  const onDelete = useCallback(
    async (whitelist) => {
      const confirm = await Alert.show({
        message: (
          <>
            <p>
              <b>Nome:</b> {whitelist?.customer_name}
            </p>
            <p>
              <b>E-mail:</b> {whitelist.customer_email}
            </p>
            <p>
              <b>CPF/CNPJ:</b> {formatDocument.apply(whitelist.customer_document)}
            </p>
          </>
        ),
        title: 'Realmente deseja excluir da whitelist o cliente abaixo?',
        confirmation: true,
      });
      if (!confirm) {
        return;
      }
      deleteWhitelist(whitelist);
      return;
    },
    [deleteWhitelist]
  );

  const editPermission = eduzzAccountsService.hasPermission('myeduzz:whitelist:editar');
  const deletePermission = eduzzAccountsService.hasPermission('myeduzz:whitelist:excluir');

  return (
    <Table id='whitelist_table' loading={isLoading} total={pagination?.total_items || 0}>
      <Table.Header>
        <Table.Column width={100}>ID</Table.Column>
        <Table.Column width={200}>Nome</Table.Column>
        <Table.Column width={250}>E-mail</Table.Column>
        <Table.Column width={200}>CPF/CNPJ</Table.Column>
      </Table.Header>

      <Table.Body>
        {whitelists?.map((wl, index) => (
          <Table.Row key={wl.id} data={wl} index={index}>
            <Table.Cell>{wl.id}</Table.Cell>
            <Table.Cell>{wl.customer_name}</Table.Cell>
            <Table.Cell>{wl.customer_email}</Table.Cell>
            <Table.Cell> {formatDocument.apply(wl.customer_document)}</Table.Cell>
            {editPermission && <Table.Action onClick={openDialog}>Editar</Table.Action>}
            {deletePermission && <Table.Action onClick={onDelete}>Excluir</Table.Action>}
          </Table.Row>
        ))}
      </Table.Body>

      {!isLoading && (
        <Table.Pagination
          page={pagination?.current_page}
          perPage={pagination?.per_page}
          onChangePage={onChangePage}
          onChangePerPage={onChangePerPage}
        />
      )}
    </Table>
  );
};

export default memo(List);
